import {
  Fragment,
  type NodeType,
  type Node as ProseMirrorNode,
  type ResolvedPos,
} from '@tiptap/pm/model';
import { type EditorState, type Transaction } from '@tiptap/pm/state';
import { convertParagraphToSection } from './apply-section-input-rule';
import { findSectionDepth } from './find-section-depth';

/**
 * Inserts an empty paragraph at the exact section boundary requested by the
 * caret. The shared `# ` conversion then treats this paragraph as its trigger:
 * it becomes the blank section heading while the right-hand content is carried
 * into the new section body.
 *
 * A direct textblock is split at the caret. A compound block such as a list or
 * table stays intact, with the trigger inserted after it.
 */
function insertEmptyTriggerAtCursor(
  tr: Transaction,
  $from: ResolvedPos,
  containerDepth: number,
  paragraph: NodeType
): ResolvedPos | null {
  const container = $from.node(containerDepth);
  const childIndex = $from.index(containerDepth);
  const cursorBlock =
    childIndex < container.childCount ? container.child(childIndex) : null;
  const trigger = paragraph.createChecked();

  // A gap selection may sit after the container's final child.
  if (!cursorBlock) {
    tr.insert($from.pos, trigger);
    return tr.doc.resolve($from.pos + 1);
  }

  const blockDepth = containerDepth + 1;
  const blockStart = $from.depth >= blockDepth ? $from.before(blockDepth) : $from.pos;

  if (
    cursorBlock.isTextblock &&
    $from.depth === blockDepth &&
    $from.parent === cursorBlock
  ) {
    const offset = $from.parentOffset;
    const before: ProseMirrorNode | null = offset > 0 ? cursorBlock.cut(0, offset) : null;
    const after: ProseMirrorNode | null =
      offset < cursorBlock.content.size ? cursorBlock.cut(offset) : null;
    const replacement = [...(before ? [before] : []), trigger, ...(after ? [after] : [])];
    tr.replaceWith(
      blockStart,
      blockStart + cursorBlock.nodeSize,
      Fragment.fromArray(replacement)
    );
    const triggerStart = blockStart + (before?.nodeSize ?? 0);
    return tr.doc.resolve(triggerStart + 1);
  }

  const triggerStart = blockStart + cursorBlock.nodeSize;
  tr.insert(triggerStart, trigger);
  return tr.doc.resolve(triggerStart + 1);
}

/**
 * Builds the transaction that turns content from the exact caret position up
 * to the next section into a new section with an empty heading to name,
 * splitting the enclosing section or wrapping loose top-level content. Returns
 * `null` when the cursor doesn't map cleanly (e.g. it sits on a section
 * heading), so the caller can fall back.
 */
export function buildConvertRestToSection(state: EditorState): Transaction | null {
  const { schema, doc, selection } = state;
  const { heading, paragraph, sectionNode } = schema.nodes;
  if (!heading || !paragraph || !sectionNode) {
    // Unreachable from the shell (it pre-checks and logs); fail null silently
    // so this pure builder carries no application logging (task 4.10).
    return null;
  }

  const $from = doc.resolve(selection.from);
  const sectionDepth = findSectionDepth($from);
  if (sectionDepth !== null && $from.index(sectionDepth) < 1) {
    // The section heading itself cannot seed a new sibling section.
    return null;
  }
  if (sectionDepth === null && $from.depth < 1) return null;

  const { tr } = state;
  const $trigger = insertEmptyTriggerAtCursor(tr, $from, sectionDepth ?? 0, paragraph);
  if (!$trigger) return null;

  // Reuse the same structural conversion as the `# ` shortcut. Its trigger
  // paragraph is deliberately empty, so it creates an untitled heading and
  // carries the caret's right-hand content into the section body.
  convertParagraphToSection(tr, $trigger, schema);
  return tr;
}
